import { Check, Mic } from "lucide-react";

import { useMicrophoneLevelTest } from "@elation/transcribe";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Label } from "@src/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";
import { Spinner } from "@src/shared/components/ui/spinner";
import { cn } from "@src/shared/lib/utils";

export function MicrophoneStep({
  onReadyChange,
}: {
  onReadyChange?: (ready: boolean) => void;
}) {
  const {
    devices,
    error,
    levels,
    selectedDeviceId,
    selectDevice,
    start,
    status,
  } = useMicrophoneLevelTest({ onReadyChange });
  const isRequesting = status === "requesting";
  const isListening = status === "listening";
  const isReady = status === "ready";
  const hasRequestedPermission = status !== "idle" && status !== "unsupported";

  return (
    <div className="flex w-full max-w-[380px] flex-col items-center gap-5">
      <div
        className={cn(
          "relative flex size-[104px] items-center justify-center rounded-full bg-muted text-primary transition-all",
          (isReady || isListening || isRequesting) &&
            "shadow-[0_12px_36px_rgba(20,23,26,0.12)]",
          isReady && "bg-success/15 text-success",
          isListening && "bg-primary/10",
        )}
      >
        {isReady ? (
          <Check className="size-[42px]" strokeWidth={2.2} aria-hidden />
        ) : isRequesting ? (
          <Spinner size="lg" />
        ) : (
          <Mic className="size-10" strokeWidth={1.9} aria-hidden />
        )}
        {isListening ? (
          <span
            className="absolute -inset-1.5 rounded-full border-2 border-primary/25 animate-pulse"
            aria-hidden
          />
        ) : null}
      </div>

      <div className="flex h-[34px] items-center gap-[5px]" aria-hidden>
        {levels.map((level, index) => (
          <span
            className={cn(
              "w-1 rounded-sm bg-border transition-[height,background-color]",
              (isListening || isReady) && "bg-primary",
              isReady && "bg-success",
            )}
            key={`${index}-${level}`}
            style={{ height: Math.max(7, Math.round(level * 62)) }}
          />
        ))}
      </div>

      <div className="w-full space-y-2 text-left">
        <Label htmlFor="sign-up-microphone-device">Microphone</Label>
        <Select
          disabled={!devices.length || isRequesting}
          onValueChange={selectDevice}
          value={selectedDeviceId ?? undefined}
        >
          <SelectTrigger
            className="h-[42px] rounded-[9px] text-[13.5px]"
            id="sign-up-microphone-device"
          >
            <SelectValue
              placeholder={
                hasRequestedPermission
                  ? "Select microphone"
                  : "Allow access to load microphones"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {devices.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <Alert
          className="border-destructive/40 bg-destructive/10 text-left"
          variant="destructive"
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isReady ? (
        <p
          className="inline-flex items-center gap-1 text-center font-mono text-[11px] text-success"
          role="status"
        >
          <Check className="size-3" strokeWidth={2.6} aria-hidden />
          audio ready - signal detected from this microphone
        </p>
      ) : (
        <p className="max-w-[320px] text-center text-xs leading-[1.5] text-muted-foreground">
          {isListening
            ? `Speak at a normal volume. The bars should move when ${PRODUCT_NAME.singular} can hear you.`
            : "Choose the microphone you use during visits, then test it before entering the assistant."}
        </p>
      )}

      <Button
        className="h-[38px] rounded-[9px] text-[13px] font-semibold"
        disabled={isRequesting}
        onClick={() => void start()}
        type="button"
      >
        {isRequesting ? (
          <Spinner className="text-primary-foreground" size="sm" />
        ) : null}
        {isReady ? "Retest microphone" : "Allow & test microphone"}
      </Button>
    </div>
  );
}
